import React, { useState, useEffect, useRef } from 'react';
import { Card, Typography } from 'antd';
import { LuTelescope } from 'react-icons/lu';

const { Text } = Typography;

const ThinkingPhase = ({ content, isCompleted = false }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const textRef = useRef(null);

  // Extract thinking content from <think> tags
  const extractThinkingContent = (text) => {
    const thinkMatch = text.match(/<think>([\s\S]*?)<\/think>/);
    return thinkMatch ? thinkMatch[1].trim() : '';
  };

  const thinkingContent = extractThinkingContent(content);

  // Auto-scroll to bottom when text updates
  useEffect(() => {
    if (textRef.current) {
      textRef.current.scrollTop = textRef.current.scrollHeight;
    }
  }, [displayedText]);

  // Initialize with current thinking content
  useEffect(() => {
    if (!thinkingContent || isInitialized) return;

    if (isCompleted) {
      // Show full content immediately if completed
      setDisplayedText(thinkingContent);
      setCurrentIndex(thinkingContent.length);
      setIsInitialized(true);
    } else {
      // Show current thinking content immediately, then continue animating new content
      setDisplayedText(thinkingContent);
      setCurrentIndex(thinkingContent.length);
      setIsInitialized(true);
    }
  }, [thinkingContent, isCompleted, isInitialized]);

  // Handle new content being added
  useEffect(() => {
    if (!thinkingContent || !isInitialized || isCompleted) return;

    // Only animate new content that's added after initialization
    const newContentLength = thinkingContent.length;
    if (newContentLength > currentIndex) {
      const interval = setInterval(() => {
        if (currentIndex < newContentLength) {
          setDisplayedText(thinkingContent.slice(0, currentIndex + 1));
          setCurrentIndex(currentIndex + 1);
        } else {
          clearInterval(interval);
        }
      }, 5); // Much faster typing speed

      return () => clearInterval(interval);
    }
  }, [thinkingContent, currentIndex, isInitialized, isCompleted]);

  if (!thinkingContent) {
    return null;
  }

  return (
    <Card 
      style={{ 
        marginBottom: '16px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #e8e8e8'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <LuTelescope 
          size="1.2em" 
          style={{ marginRight: '8px', color: '#1890ff' }} 
        />
        <Text strong style={{ color: '#1890ff' }}>
          Deep Research Thinking...
        </Text>
      </div>
      <div 
        ref={textRef}
        style={{ 
          backgroundColor: '#fff', 
          padding: '16px', 
          borderRadius: '6px',
          border: '1px solid #f0f0f0',
          maxHeight: '300px',
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
          fontFamily: 'monospace',
          fontSize: '13px',
          lineHeight: '1.5',
          color: '#666'
        }}
      >
        {displayedText}
        {!isCompleted && currentIndex < thinkingContent.length && (
          <span style={{ 
            display: 'inline-block',
            width: '2px',
            height: '16px',
            backgroundColor: '#1890ff',
            marginLeft: '2px',
            animation: 'blink 1s infinite'
          }} />
        )}
      </div>
      <style jsx>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </Card>
  );
};

export default ThinkingPhase; 